import { createRef, useCallback, useEffect, useMemo } from 'react';
import { IStepper, StepType } from "../Stepper";
import { StepIndex, StepIndexUnlocked, StepIndexSubmiting, StepperStore, StepIndexAtom } from '../state/StepperState';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { useDebounce } from 'hooks/useDebounce';

const useStepper = (props: IStepper) => {

    const debounce = useDebounce()
    const setStepIndex = useSetRecoilState(StepIndexAtom)
    const [stepIndex] = useRecoilState(StepIndex)
    const [stepUnlocked, setStepUnlocked] = useRecoilState(StepIndexUnlocked);
    const [stepIndexSubmiting, setStepIndexSubmiting] = useRecoilState(StepIndexSubmiting);
    const setStepperStore= useSetRecoilState(StepperStore)

    const divsRef = createRef<HTMLDivElement>()
    const layoutContainerRef = createRef<HTMLDivElement>()

    const stepIndexControlled = useMemo(() => props.stepIndex !== undefined, [])
    const step = useMemo(() => props.steps[stepIndex], [props.steps, stepIndex])

    useEffect(() => {

        return () => {
            // Store reseting on unmount
            setStepIndex(0)
            setStepUnlocked(0)
            setStepperStore({})
        }
    }, [])

    useEffect(() => {
        if (props.stepIndex && props.stepIndex !== stepIndex) {
            setStepIndex(props.stepIndex)
        }
    }, [props.stepIndex])
    
    useEffect(() => {
        if (stepIndex > stepUnlocked) {
            setStepUnlocked(stepIndex)
        }
        setStepIndexSubmiting(undefined)
    }, [stepIndex])

    useEffect(() => {
        if(stepUnlocked !== 0 && props.layout === 'scroll') {
            scrollToStep(stepUnlocked)
        }
    }, [stepUnlocked, props.layout])

    useEffect(() => {
        if (stepUnlocked >= 0 && props.layout === 'next') {
            setStepIndex(stepUnlocked)
        }
    }, [stepUnlocked]);

    const handleScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
        if (!divsRef.current) return;
        let index = 0;
        if( !props.isLastStepCanGoBack && props.steps.length - 1  == stepUnlocked) {
            index = stepUnlocked
        } else {
            Array.from(divsRef.current.children)
            .forEach((item, i) => {
                if ((item as HTMLDivElement).offsetTop <= (e.currentTarget.scrollTop + e.currentTarget.offsetHeight)) {
                    index = i
                }
            });
        }
        index !== -1 && debounce(() => setStepIndex(index), 50)
    }

    const handleLabelClick = (step: StepType, index: number) => {
        scrollToStep(index)
    }

    const scrollToStep = useCallback((index: number) => {
        const child = divsRef.current?.children[index] as HTMLDivElement
        if (child) {
            layoutContainerRef.current?.scroll({ behavior: 'smooth', top: child.offsetTop })
        }
    }, [divsRef, layoutContainerRef])

    const handleFormSubmit = async (value?: Record<string, any>, step?: StepType, index?: number) => {

        if (stepIndexControlled) {
            props.onStepSubmit?.(value, step);
            setStepIndexSubmiting(index)
            return;
        }


        // callback on step submit
        setStepIndexSubmiting(index)
        try {
            await props.onStepSubmit?.(value, step);
        } catch (e) {
        	throw e
        } finally {
            setStepIndexSubmiting(undefined)
            // end of the step
            if (index === props.steps.length - 1) {
                return props.onStepperEnd?.(value)
            }
            setStepUnlocked((index || 0) + 1);
        }
    }

    const handleBack = () => {
        if (stepIndex > 0) {
            setStepUnlocked((index) => index - 1)
        }
    }

    return {
        stepIndex,
        setStepIndex,
        stepUnlocked,
        stepIndexSubmiting,
        step,
        handleFormSubmit,
        handleBack,
        handleScroll,
        handleLabelClick,
        divsRef,
        layoutContainerRef
    }
}

export default useStepper;